// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PasswordInput } from '@/app/components/password-input';
import {
  useLogin,
  loginSchema,
  LoginCodeForm,
  OAuthButtons,
  RecaptchaGuard,
  useCaptchaToken,
  useAuthConfig,
} from '@/app/features/auth';
import type { LoginFormData } from '@/app/features/auth';

function LoginForm() {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});
  const login = useLogin();
  const getCaptchaToken = useCaptchaToken();
  const { googleAuthEnabled, githubAuthEnabled } = useAuthConfig();

  const hasOAuth = googleAuthEnabled || githubAuthEnabled;

  // Read OAuth error from URL query params (set by backend on OAuth failure redirect).
  const oauthError = new URLSearchParams(window.location.search).get('error');

  // After a correct password the server may still want the emailed code
  // before it issues a session; the card then switches to the code step.
  const challenge = login.data?.kind === 'challenge' ? login.data : null;

  function abandonChallenge() {
    login.reset();
    setFormData((prev) => ({ ...prev, password: '' }));
  }

  function handleChange(field: keyof LoginFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof LoginFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof LoginFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const captchaToken = await getCaptchaToken('login');
    login.mutate({ ...result.data, captchaToken });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <a href="/" className="mb-8 flex items-center justify-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          <span className="text-[24px] font-semibold tracking-tight">FlairCV</span>
        </a>

        {/* Login form */}
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {challenge ? (
            <LoginCodeForm challenge={challenge} onBack={abandonChallenge} />
          ) : (
            <>
              <h1 className="text-[18px] font-semibold text-card-foreground">Sign in</h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Enter your email and password to access your account.
              </p>

              {/* OAuth error */}
              {oauthError && (
                <div
                  className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
                  role="alert"
                >
                  {oauthError}
                </div>
              )}

              {/* Server error */}
              {login.error && (
                <div
                  className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
                  role="alert"
                >
                  {login.error.message}
                </div>
              )}

              <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-[13px] font-medium text-foreground"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    aria-invalid={!!fieldErrors.email}
                    aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  />
                  {fieldErrors.email && (
                    <p id="email-error" className="mt-1 text-[12px] text-destructive">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="block text-[13px] font-medium text-foreground"
                    >
                      Password
                    </label>
                    <Link
                      to="/auth/forgot-password"
                      className="text-[12px] font-medium text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                  />
                  {fieldErrors.password && (
                    <p id="password-error" className="mt-1 text-[12px] text-destructive">
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={login.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  {login.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>

              {hasOAuth && (
                <>
                  <div className="relative mt-6 mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-[12px]">
                      <span className="bg-card px-2 text-muted-foreground">or</span>
                    </div>
                  </div>

                  <OAuthButtons />
                </>
              )}

              <div className="mt-4 text-center text-[13px] text-muted-foreground">
                Don&apos;t have an account?{' '}
                <Link
                  to="/auth/register"
                  className="font-medium text-primary hover:underline"
                >
                  Create one
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  return (
    <RecaptchaGuard>
      <LoginForm />
    </RecaptchaGuard>
  );
}
